import { yupResolver } from '@hookform/resolvers/yup';
import { spaces } from '@kimuno/theme';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import { useCallback, useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { selectUser } from '../../reducers/user.selectors';
import services from '../../services';
import { NotificationContext } from '../../state/NotificationContextProvider';
import useScreenSize from '../../utils/useScreenSize';
import { password } from '../../validators';
import EditableSection from '../EditableSection';
import EmailFieldsRenderer from '../EmailFieldsRenderer';

export interface ChangeEmailPayload {
	email: string;
	password: string;
}

interface ServerErrorMessage {
	field: keyof ChangeEmailPayload;
	message: string;
}

const ChangeEmailSection = () => {
	const { t } = useTranslation();
	const { showNotification } = useContext(NotificationContext);
	const { isXsScreen, isSmScreen } = useScreenSize();
	const [isEditMode, setIsEditMode] = useState(false);
	const [pendingEmail, setPendingEmail] = useState<string>();

	const user = useSelector(selectUser);
	const canEditEmail = () => {
		return !!user && !user.isSchoolUser && !user.isLibraryUser && !user.isKeycloakSSO;
	};
	const userEmail: string = user?.email || '';

	const changeEmailFormValidator = yup.object({
		email: yup
			.string()
			.email('The email is not valid')
			.required('This field is required')
			.notOneOf([userEmail], 'This email address is the same as your current email address'),
		password: password(),
	});

	const {
		register,
		formState: { errors },
		getValues,
		trigger,
		setError,
	} = useForm<ChangeEmailPayload>({
		mode: 'onTouched',
		shouldUnregister: true,
		resolver: yupResolver(changeEmailFormValidator),
		defaultValues: { email: userEmail },
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	const onSaveChanges = useCallback(
		async ({ toggleEditMode }: { toggleEditMode: () => void }) => {
			const isValid = await trigger(['email', 'password']);

			if (!isValid) return;

			const values = getValues();

			if (!user) return;

			try {
				await services.user.changeEmail(user.email, values.email, values.password, false);
				toggleEditMode();
				setPendingEmail(values.email);
			} catch (error: any) {
				showNotification({
					message: t('An error occurred while saving changes'),
					severity: 'error',
				});
				const serverErrorMessage = getServerErrorMessage(error.message);
				if (serverErrorMessage) {
					return setError(serverErrorMessage.field, {
						type: 'manual',
						message: t(serverErrorMessage.message),
					});
				}
			}
		},
		[user]
	);

	const getServerErrorMessage = (errorMessage: string): ServerErrorMessage | undefined => {
		if (errorMessage.match(/Invalid username or password/)) {
			return {
				field: 'password',
				message: 'The password you entered is incorrect.',
			};
		}
		if (errorMessage.match(/Email address already exists/)) {
			return {
				field: 'email',
				message: 'The email you entered already exists.',
			};
		}
		return;
	};

	return (
		<Box
			sx={{
				display: 'grid',
				gridTemplateColumns: isXsScreen || isSmScreen ? '1fr' : '1fr 1fr',
				gap: spaces.md,
			}}
		>
			<EditableSection
				title={t('My Account')}
				onSaveChanges={onSaveChanges}
				isEditMode={isEditMode}
				onEditModeChange={setIsEditMode}
			>
				{({ isEditMode }) => (
					<>
						<EmailFieldsRenderer
							isEditMode={isEditMode}
							userEmail={userEmail}
							canEditEmail={canEditEmail()}
							register={register}
							trigger={trigger}
							errors={errors}
						/>
						{pendingEmail && (
							<Alert severity="info" sx={{ marginTop: spaces.md }}>
								<AlertTitle color="inherit">{t('Almost done!')}</AlertTitle>
								<Typography variant="body2" color="inherit">
									{t("We've sent an email to {{email}}. Click the link in it to complete the change.", {
										email: pendingEmail,
									})}
								</Typography>
							</Alert>
						)}
					</>
				)}
			</EditableSection>
		</Box>
	);
};

export default ChangeEmailSection;
